import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Button } from '@mui/material';
import ModalForms from '../Shared/Modals/index.js';
import CommonTable from '../Shared/Tables/TableV1.tsx';
import LongStringView from '../Shared/LongStringView/index.tsx';
import { dateFormate } from '../../utils/helper.jsx';
import { ConstantActions } from '../../Services/_actions';
import APIService from '../../Services/APIService.js';

interface AudioSummaryModalProps {
  open: boolean;
  onClose: () => void;
  rowData: any;
  setTableState: React.Dispatch<React.SetStateAction<any>>;
  dataList: any[];
}

const AudioSummaryModal = ({ open, onClose, rowData, setDataList, dataList, setTableState }) => {
  const [audioFile, setAudioFile] = useState < File | null > (null);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState < any > (null);
  const constantActions = ConstantActions();
  const fileInputRef = useRef(null);

  const handleUpload = async () => {
    if (!audioFile || !rowData?.lead_id) return;
    try {
      setLoading(true);
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', audioFile);
      // Step 1: Upload file to S3
      // const myFilename = `${Date.now().toString()}${audioFile.name}`.replace(/ /g, '_');
      // const updatedFile = new File([audioFile], myFilename, { type: audioFile.type });

      const resp = await constantActions.uploadFileS3Bucket(updatedFile);
      let audioLink = '';

      // if (resp?.$response?.httpResponse?.statusCode === 200) {
      //   audioLink = `${process.env.REACT_APP_IMAGE_URL}/${myFilename}`;
      // }

      if (resp?.status === 200 && resp?.data?.data) {
        audioLink = resp?.data?.data?.url ?? '';
      } else {
        throw new Error('Failed to upload audio to S3');
      }

      // Step 2: Send audio file + link to transcription API
      const formData = new FormData();
      formData.append('audio', audioFile); // required by backend
      formData.append('audio_link', audioLink); // optional link

      const url = `v0/ui/ai-agent/audio-to-text/?lead_id=${encodeURIComponent(rowData.lead_id)}`;
      const response = await APIService.uploadFile(url, formData);
      const result = response.data;

      setResponseData(result);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // This clears the input
      }
      setAudioFile(null);
      // Step 3: Update the table with summary

      setTableState((prevState) => {
        const updatedRows = prevState?.data?.map((row) => row.id === rowData.id ? { ...row, audio_summary: result } : row);
        return { ...prevState, data: updatedRows };
      });
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setLoading(false);
    }
  };

  const columns1 = useMemo(
    () => [
      {
        accessorKey: 'transcript',
        header: 'Transcript',
        enableSorting: false,
        Cell: ({ cell, row }) => <LongStringView longString={row.original.transcript} />,
      },
      {
        accessorKey: 'summary',
        header: 'Summary',
        enableSorting: false,
        Cell: ({ cell, row }) => <LongStringView longString={row.original.summary} />,
      },
      {
        accessorKey: 'audio_link',
        header: 'Audio',
        enableSorting: false,
        Cell: ({ cell, row }) => {
          const audioLink = row.original?.audio_link || '';

          return (
            <>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <audio controls style={{ maxWidth: '100%' }}>
                  <source src={audioLink} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              </div>
              <div>
                {dateFormate(row.original?.audio_summary?.start_time, 'DD-MM-YYYY hh:mm:ss A')}
              </div>
            </>
          );
        },
      },
    ],
    [dataList]
  );
  return (
    <ModalForms
      show={open}
      handleClose={onClose}
      modalHeader="Audio Summary"
      modalBody={
        <Box>
          <Box sx={{ mt: 2, mb: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
              style={{ marginBottom: 10 }}
            />
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!audioFile || loading}
              sx={{ ml: 1 }}
            >
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          </Box>
          <CommonTable
            columns={columns1}
            data={dataList}
            enableBottomToolbar={false}
            muiTableContainerProps={{
              sx: {
                height: 'calc(100vh - 300px)',
                width: '100%',
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '6px' },
              },
            }}
            state={{ isLoading: loading }}
          />
        </Box>
      }
    />
  );
};

export default AudioSummaryModal;
